import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ScanFace, ExternalLink, X, Plus, Images } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { fetchBackgroundsAll, saveBackgroundScope } from '../../services/site';
import { uploadMediaFiles } from '../../services/media';
import { ImageLibrary } from '../../components/admin/ImageLibrary';

const SCOPE_LABELS = {
  default: 'Toutes les pages (par défaut)', home: 'Accueil', membership: 'Adhésion',
  contact: 'Contact', donate: 'Dons', gallery: 'Galerie', shop: 'Boutique',
  blog: 'Blog & Tracts', faq: 'FAQ', carte: 'Carte', equipe: 'Équipe',
};
const SCOPE_ROUTE = {
  default: '/', home: '/', membership: '/membership', contact: '/contact', donate: '/donate',
  gallery: '/gallery', shop: '/shop', blog: '/blog', faq: '/faq', carte: '/carte', equipe: '/equipe',
};

const THEMES = [
  { key: 'dark', arr: 'dark_images', pos: 'dark_position', int: 'dark_intensity', label: 'Mode nuit (thème sombre)', defInt: 50 },
  { key: 'light', arr: 'light_images', pos: 'light_position', int: 'light_intensity', label: 'Mode éclairé (thème clair)', defInt: 60 },
];
const POSITIONS = [
  { v: 'center', l: 'Centré' }, { v: 'top', l: 'Haut' }, { v: 'bottom', l: 'Bas' },
];

// Normalise un scope brut en form éditable (images en tableau)
const toForm = (raw) => {
  const f = { ...(raw || {}) };
  ['light', 'dark'].forEach((t) => {
    let arr = f[`${t}_images`];
    if (!Array.isArray(arr) || !arr.length) arr = f[`${t}_image`] ? [f[`${t}_image`]] : [];
    f[`${t}_images`] = arr;
    delete f[`${t}_image`];
  });
  return f;
};

const BackgroundSettings = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scopeKeys, setScopeKeys] = useState(['default']);
  const [allScopes, setAllScopes] = useState({});
  const [scope, setScope] = useState('default');
  const [form, setForm] = useState({});
  const [libOpen, setLibOpen] = useState(false);
  const [libTarget, setLibTarget] = useState('dark_images');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchBackgroundsAll(token);
        setScopeKeys(data.scope_keys || ['default']);
        setAllScopes(data.scopes || {});
        setForm(toForm((data.scopes || {})['default']));
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  if (!user || user.user_type !== 'fondateur') {
    return <div className="p-6 text-sm text-red-500">Accès réservé au fondateur.</div>;
  }

  const onScopeChange = (s) => {
    setScope(s);
    setForm(toForm(allScopes[s]));
    setSuccess(''); setError('');
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const addImage = (arrKey, url) => {
    if (!url) return;
    setForm((f) => ({ ...f, [arrKey]: [...(f[arrKey] || []), url] }));
  };
  const addImages = (arrKey, urls) => {
    if (!urls || !urls.length) return;
    setForm((f) => ({ ...f, [arrKey]: [...(f[arrKey] || []), ...urls] }));
  };
  const removeImage = (arrKey, idx) => {
    setForm((f) => ({ ...f, [arrKey]: (f[arrKey] || []).filter((_, i) => i !== idx) }));
  };

  const onUpload = async (arrKey, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      setUploading(arrKey); setError('');
      const res = await uploadMediaFiles(files, token);
      const urls = (res?.uploaded || []).map((u) => u.url).filter(Boolean);
      addImages(arrKey, urls);
      if (res?.errors?.length) setError(`${res.errors.length} fichier(s) refusé(s) : ${res.errors.map((e) => e.name).join(', ')}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading('');
    }
  };

  const onSave = async () => {
    try {
      setSaving(true); setError(''); setSuccess('');
      await saveBackgroundScope(scope, form, token);
      setAllScopes((s) => ({ ...s, [scope]: form }));
      setSuccess(`✅ Fond enregistré pour « ${SCOPE_LABELS[scope] || scope} ».`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="background-settings-page">
      <Button asChild variant="ghost" size="sm"><Link to="/dashboard/fondateur/admin"><ArrowLeft className="h-4 w-4 mr-2" />Centre d'administration</Link></Button>
      <Card>
        <CardHeader>
          <CardTitle>Fonds d'écran par page (jour / nuit)</CardTitle>
          <CardDescription>Pour chaque page, ajoutez une ou plusieurs photos (elles défilent à chaque visite), réglez le cadrage et l'intensité du voile, par thème. Les pages sans réglage héritent du fond « Toutes les pages ».</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && <Alert><AlertDescription className="text-red-600" data-testid="bg-error">{error}</AlertDescription></Alert>}
          {success && <Alert><AlertDescription className="text-green-600" data-testid="bg-success">{success}</AlertDescription></Alert>}

          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Par défaut, les pages affichent une photo <strong>au hasard</strong> de la bibliothèque de fonds et en changent automatiquement. Réglez ce pool ici :</p>
            <Button asChild variant="outline" size="sm" data-testid="bg-goto-library"><Link to="/admin/backgrounds/library"><Images className="h-4 w-4 mr-2" />Bibliothèque de fonds</Link></Button>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">Les réglages ci-dessous ne concernent qu'une page précise (ils remplacent le pool pour cette page uniquement).</p>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-sm mb-1 font-medium">Page à configurer</label>
              <select data-testid="bg-scope-select" value={scope} onChange={(e) => onScopeChange(e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                {scopeKeys.map((k) => <option key={k} value={k}>{SCOPE_LABELS[k] || k}</option>)}
              </select>
            </div>
            <Button asChild variant="outline" data-testid="bg-preview-btn">
              <a href={SCOPE_ROUTE[scope] || '/'} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Voir la page</a>
            </Button>
          </div>

          <label className="flex items-center gap-3 rounded-lg border border-border p-4 cursor-pointer select-none">
            <input type="checkbox" className="h-4 w-4 rounded border-border accent-primary" data-testid="bg-enabled" checked={form.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} />
            <span className="text-sm">Afficher un fond photo sur cette page (décocher pour un fond uni)</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {THEMES.map(({ key, arr, pos, int, label, defInt }) => {
              const images = form[arr] || [];
              const intensity = form[int] != null ? form[int] : defInt;
              const position = form[pos] || 'center';
              return (
                <div key={key} className="rounded-lg border border-border p-4 space-y-3">
                  <p className="text-sm font-semibold">{label}</p>

                  {/* Liste d'images (rotation) */}
                  <div className="space-y-2">
                    {images.length === 0 && <p className="text-xs text-muted-foreground">Aucune image — hérite du fond par défaut.</p>}
                    {images.map((url, idx) => (
                      <div key={idx} className="relative h-20 rounded-md overflow-hidden border border-border group">
                        <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: position === 'top' ? 'center top' : position === 'bottom' ? 'center bottom' : 'center' }} />
                        <div className="absolute inset-0" style={{ backgroundColor: key === 'light' ? `hsl(0 0% 100% / ${intensity / 100})` : `hsl(165 30% 6% / ${intensity / 100})` }} />
                        <button type="button" onClick={() => removeImage(arr, idx)} data-testid={`bg-remove-${key}-${idx}`} className="absolute top-1 right-1 z-10 rounded-full bg-black/60 text-white p-1 hover:bg-red-600"><X className="h-3.5 w-3.5" /></button>
                        {images.length > 1 && <span className="absolute bottom-1 left-1 z-10 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">#{idx + 1}</span>}
                      </div>
                    ))}
                  </div>

                  {/* Ajouter une image */}
                  <div className="flex items-center gap-2">
                    <input type="file" accept="image/*" multiple data-testid={`bg-upload-${key}`} onChange={(e) => onUpload(arr, e.target.files)} className="text-xs flex-1" />
                    {uploading === arr && <span className="text-xs text-muted-foreground">…</span>}
                    <Button type="button" size="sm" variant="outline" data-testid={`bg-library-${key}`} onClick={() => { setLibTarget(arr); setLibOpen(true); }}><Images className="h-4 w-4 mr-1" />Bibliothèque</Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input data-testid={`bg-url-${key}`} placeholder="Coller une URL /uploads/… puis +" className="text-xs" onKeyDown={(e) => { if (e.key === 'Enter') { addImage(arr, e.target.value.trim()); e.target.value = ''; } }} id={`bg-urlinput-${key}`} />
                    <Button type="button" size="sm" variant="outline" data-testid={`bg-addurl-${key}`} onClick={() => { const el = document.getElementById(`bg-urlinput-${key}`); if (el?.value.trim()) { addImage(arr, el.value.trim()); el.value = ''; } }}><Plus className="h-4 w-4" /></Button>
                  </div>

                  {/* Position / cadrage */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">Cadrage</label>
                    <select data-testid={`bg-position-${key}`} value={position} onChange={(e) => set(pos, e.target.value)} className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs">
                      {POSITIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                    </select>
                  </div>

                  {/* Intensité */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">Voile</label>
                    <input type="range" min="0" max="95" value={intensity} data-testid={`bg-intensity-${key}`} onChange={(e) => set(int, Number(e.target.value))} className="flex-1 accent-primary" />
                    <span className="text-xs w-8 text-right">{intensity}%</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-lg border border-border p-4 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium">Défilement automatique</label>
            <input
              type="number" min="0" max="120"
              data-testid="bg-rotate-interval"
              value={form.rotate_interval != null ? form.rotate_interval : 0}
              onChange={(e) => set('rotate_interval', Math.max(0, Math.min(120, Number(e.target.value) || 0)))}
              className="w-24 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <span className="text-sm text-muted-foreground">secondes — 0 = change seulement à chaque visite. Nécessite au moins 2 photos.</span>
          </div>

          <div className="rounded-lg border border-dashed border-border p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Besoin de masquer des visages avant d'utiliser une photo ?</p>
            <Button asChild variant="outline" size="sm" data-testid="bg-goto-blur"><Link to="/dashboard/fondateur/floutage"><ScanFace className="h-4 w-4 mr-2" />Outil de floutage</Link></Button>
          </div>

          <div className="flex justify-end">
            <Button data-testid="bg-save-btn" onClick={onSave} disabled={saving}>{saving ? 'Enregistrement…' : `Sauvegarder « ${SCOPE_LABELS[scope] || scope} »`}</Button>
          </div>
        </CardContent>
      </Card>

      <ImageLibrary open={libOpen} onOpenChange={setLibOpen} onSelect={(url) => addImage(libTarget, url)} />
    </div>
  );
};

export default BackgroundSettings;
